import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { createFileRoute } from "@tanstack/react-router"
import {
  BigPicture,
  CodeAndParts,
  OnTop,
  SpecialMeters,
  WhenMissing,
  WhichMeter,
} from "@/features/list-of-materials/components/learn/Lessons"
import { Block } from "@/features/list-of-materials/components/ui/Block"
import { TextLink } from "@/features/list-of-materials/components/ui/Button"
import { PageHead } from "@/features/list-of-materials/components/ui/PageHead"
import { useSelectedLomVersion } from "@/features/list-of-materials/hooks/useLomVersion"

export const Route = createFileRoute("/(authenticated)/list-of-materials/learn")({
  component: LearnPage,
})

function LearnPage() {
  const { versionSearch } = useSelectedLomVersion()
  const lessons = [
    t`The big picture`,
    t`Which meter is which`,
    t`From number to code, from code to parts`,
    t`Special meters and the rules`,
    t`What comes on top, and where the list goes`,
    t`When information is missing`,
  ]
  return (
    <>
      <PageHead
        title={<Trans>Learn</Trans>}
        sub={
          <Trans>
            Six short lessons on how a store's list of materials is made, each with an
            example from Action's data and a question to check yourself.
          </Trans>
        }
      />

      <div className="mb-8 grid max-w-4xl grid-cols-2 gap-5 max-[900px]:grid-cols-1">
        <Block className="px-4 py-3">
          <div className="text-lom-ink-3 mb-2 text-xs font-medium tracking-wider uppercase">
            {t`Lessons`}
          </div>
          <ol className="flex list-decimal flex-col gap-1 pl-5 text-[15px]">
            {lessons.map((title, i) => (
              <li key={title}>
                <a
                  href={`#lesson-${i + 1}`}
                  className="text-lom-accent-ink hover:underline"
                >
                  {title}
                </a>
              </li>
            ))}
          </ol>
        </Block>
        <Block className="px-4 py-3">
          <div className="text-lom-ink-3 mb-2 text-xs font-medium tracking-wider uppercase">
            {t`One meter in five steps`}
          </div>
          <ol className="flex list-decimal flex-col gap-1 pl-5 text-[15px]">
            <li>{t`Number the meter within its group.`}</li>
            <li>{t`Read its shelf code in the coding.`}</li>
            <li>{t`Take the shelf code's parts.`}</li>
            <li>{t`Apply the rules for its setup.`}</li>
            <li>{t`Add the extra per run at the end of a run.`}</li>
          </ol>
          <p className="text-lom-ink-2 mt-2 text-sm">
            {t`Fixtures and extras per store come on top.`}{" "}
            <TextLink to="/list-of-materials/how-it-works" search={versionSearch}>
              {t`See it on a real meter in How it works`}
            </TextLink>
          </p>
        </Block>
      </div>

      <BigPicture />
      <WhichMeter />
      <CodeAndParts />
      <SpecialMeters />
      <OnTop />
      <WhenMissing />
    </>
  )
}
